<template>
    <div class="top-bar" tabindex="-1">
        <div class="top-bar-leading">
            <div class="top-bar-sidebar-button">
                <button type="button" class="top-bar-btn" :title="$t('Main menu')" @click="clickMenu">
                    <img src="@/assets/svg/menu_open.svg" class="icon-svg-perso" />
                </button>
            </div>
            <NavigationBreadcrumb v-if="loggedIn" />
        </div>
        <div v-if="loggedIn" class="top-bar-buttons">
            <LanguageSelectMenu />
            <div class="top-bar-icons">
                <UseLLmButton v-if="canUseLLm" @openAssistant="showChatAssistant" />
                <NotificationsButton
                    :visible-notifications-count="visibleNotificationsCount"
                    :can-notifications="canNotifications"
                    @notifications-count-change="onNotificationsCountChange"
                />
                <UserSettingsButton
                    :profile-name="profileName"
                    :profile-image="profileImage"
                    :uid="uid"
                    :can-admin="canAdmin"
                    :can-config-data-space="canConfigDataSpace"
                    :render-language="renderLanguage"
                    :get-language="actualLanguage"
                    @select-language="selectLanguage"
                    @open-onboarding="openOnboarding"
                    @open-logout-modal="openLogOutModal"
                />
            </div>
        </div>
        <div class="top-bar-icons" v-if="!loggedIn">
            <LanguageSelectMenu />
            <button type="button" @click="login" class="btn btn-primary btn-top-bar-login">
                <i class="fas fa-sign-in"></i> {{ $t("Login") }}
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import { AuthController } from "@/control/auth";
import { NotificationsController } from "@/control/notifications";
import { defineComponent, markRaw } from "vue";
import { AVAILABLE_LANGUAGES, getLanguage, setLanguage } from "@/i18n";
import LanguageSelectMenu from "../dropdowns/language/LanguageSelectMenu.vue";
import LogoutModalContent from "../modals/auth/LogoutModalContent.vue";
import UserSettingsButton from "@/components/buttons/TopBar/UserSettingsButton.vue";
import NotificationsButton from "../buttons/TopBar/NotificationsButton.vue";
import UseLLmButton from "@/components/buttons/TopBar/UseLLmButton.vue";
import NavigationBreadcrumb from "@/components/navigation/NavigationBreadcrumb.vue";

const REQUIRED_ADMIN_PERMISSIONS = ["mod.users", "admin.roles"];

type NotificationCountChangeOptions = {
    optimistic?: boolean;
};

export default defineComponent({
    components: {
        LanguageSelectMenu,
        UserSettingsButton,
        NotificationsButton,
        UseLLmButton,
        NavigationBreadcrumb,
    },
    name: "TopBar",
    emits: ["toggle-menu", "openModal"],
    data() {
        return {
            platformName: import.meta.env.VITE__PLATFORM_NAME || "Platform",
            loggedIn: AuthController.isAuthenticated(),
            profileImage: AuthController.ProfileImage,
            profileName: AuthController.ProfileName || AuthController.Username || "",
            uid: AuthController.UID || "",
            canNotifications: AuthController.hasPermission("notification.read"),
            notificationsCount: NotificationsController.UnreadNotificationsCount,
            notificationsCountOverride: null as number | null,

            canUseLLm: AuthController.hasPermission("llm.use") && AuthController.SidecarLLM.assistant,
            canAdmin: REQUIRED_ADMIN_PERMISSIONS.some((p) => AuthController.hasPermission(p)),
            canConfigDataSpace: AuthController.hasPermission("admin.ds"),
        };
    },
    methods: {
        selectLanguage: function () {
            const currentLang = getLanguage();
            const currentIndex = AVAILABLE_LANGUAGES.findIndex((l) => l.id === currentLang);
            const nextIndex = (currentIndex + 1) % AVAILABLE_LANGUAGES.length;
            setLanguage(AVAILABLE_LANGUAGES[nextIndex].id);
        },

        showChatAssistant: function () {
            this.$emit("openModal", "chat-assistant");
        },

        login: function () {
            this.$requireLogin();
        },

        openOnboarding: function () {
            this.$triggerAppEvent("onboarding-open");
        },

        clickMenu: function () {
            this.$emit("toggle-menu");
        },

        onAuthChanged: function () {
            this.loggedIn = AuthController.isAuthenticated();
            this.profileName = AuthController.ProfileName || AuthController.Username || "";
            this.uid = AuthController.UID || "";
            this.profileImage = AuthController.ProfileImage;
            this.canUseLLm = AuthController.hasPermission("llm.use") && AuthController.SidecarLLM.assistant;
            this.canNotifications = AuthController.hasPermission("notification.read");
            this.canAdmin = REQUIRED_ADMIN_PERMISSIONS.some((p) => AuthController.hasPermission(p));
            this.canConfigDataSpace = AuthController.hasPermission("admin.ds");
            if (!this.canNotifications) {
                this.notificationsCount = 0;
                this.notificationsCountOverride = null;
            }
        },

        renderLanguage: function (lang: string): string {
            for (const language of AVAILABLE_LANGUAGES) {
                if (language.id === lang) {
                    return language.name;
                }
            }
            return "-";
        },

        onNotificationsCountChange: function (count: number, options?: NotificationCountChangeOptions) {
            this.notificationsCount = count;
            this.notificationsCountOverride = options?.optimistic && count === 0 ? 0 : null;
        },

        openLogOutModal: function () {
            this.$openModal({
                title: this.$t("Confirm logout"),
                message: this.$t("Do you want to log out of the application?"),
                customComponent: markRaw(LogoutModalContent),
                displayFooter: false,
                positionIconHeader: "none",
                modalType: "info",
            });
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", this.onAuthChanged.bind(this));
        this.$listenOnAppEvent("notification-count-update", () => {
            this.notificationsCount = NotificationsController.UnreadNotificationsCount;
            this.notificationsCountOverride = null;
        });
    },
    beforeUnmount: function () {},
    computed: {
        visibleNotificationsCount: function (): number {
            return this.notificationsCountOverride ?? this.notificationsCount;
        },

        actualLanguage: function () {
            return getLanguage();
        },
    },
});
</script>

<style>
.top-bar-user-menu .dropdown-menu-item.has-separator {
    border-top: 1px solid var(--color-border-default);
    border-bottom: none !important;
}

.top-bar-user-menu .dropdown-menu-item {
    padding: 16px;
}
</style>
